import { throttle } from "lodash";
import { useEffect, useRef, useState } from "react";

/**
 * Custom hook to manage sticky action bar positioning based on scroll
 *
 * This hook monitors the scroll position of the content container and determines
 * whether the action bar should be fixed to the viewport or fixed to the component bottom.
 * Uses hysteresis to prevent rapid toggling and reduce jumpiness.
 *
 * @param dependencies - Optional dependencies array to trigger recalculation of scroll behavior
 * @returns boolean indicating whether the action bar should be fixed to viewport
 */
export function useStickyActionBar(dependencies: any[] = []): boolean {
  const [actionBarFixed, setActionBarFixed] = useState(false);
  const lastStateRef = useRef(false);

  useEffect(() => {
    // Find the scrollable content container
    const contentWithFooter = document.getElementById("content-with-footer");
    const threadContainer = document.querySelector(
      ".thread-preview-mobile-container"
    );

    if (!contentWithFooter || !threadContainer) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, clientHeight } = contentWithFooter;

      // Get the position of the thread container relative to the scroll container
      const threadRect = threadContainer.getBoundingClientRect();
      const contentRect = contentWithFooter.getBoundingClientRect();

      // Calculate the bottom of the thread container relative to the scroll container
      const threadBottom = threadRect.bottom - contentRect.top + scrollTop;

      // Calculate how much of the thread content is visible
      const visibleBottom = scrollTop + clientHeight;
      const distanceToThreadBottom = threadBottom - visibleBottom;

      // Use hysteresis to prevent rapid toggling
      // When switching from fixed to non-fixed, require distance < 100px
      // When switching from non-fixed to fixed, require distance > 140px
      // This creates a 40px buffer zone to prevent jumpiness
      const THRESHOLD_TO_FIXED = 70;
      const THRESHOLD_TO_UNFIXED = 50;

      let shouldBeFixed: boolean;

      if (lastStateRef.current) {
        // Currently fixed - use lower threshold to unfix
        shouldBeFixed = distanceToThreadBottom > THRESHOLD_TO_UNFIXED;
      } else {
        // Currently not fixed - use higher threshold to fix
        shouldBeFixed = distanceToThreadBottom > THRESHOLD_TO_FIXED;
      }

      // Only update state if it actually changed
      if (shouldBeFixed !== lastStateRef.current) {
        lastStateRef.current = shouldBeFixed;
        setActionBarFixed(shouldBeFixed);
      }
    };

    // Use requestAnimationFrame for smoother updates
    const handleScrollSmooth = () => {
      requestAnimationFrame(handleScroll);
    };

    // Create throttled version to limit execution frequency
    const throttledHandleScroll = throttle(handleScrollSmooth, 100);

    // Initial check
    handleScroll();

    // Add scroll event listener
    contentWithFooter.addEventListener("scroll", throttledHandleScroll);

    // Add resize event listener to handle orientation changes and window resizing
    window.addEventListener("resize", throttledHandleScroll);

    return () => {
      contentWithFooter.removeEventListener("scroll", throttledHandleScroll);
      window.removeEventListener("resize", throttledHandleScroll);
      // Cancel any pending throttled calls
      throttledHandleScroll.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  return actionBarFixed;
}
